import { makeAutoObservable, reaction, runInAction } from "mobx";
import { type SearchResult } from "../Pages/MapPage/@types";
import { apiCall } from "../Api";
import type AppStore from "./AppStore";
import { type AxiosInstance, type AxiosResponse } from "axios";
import { CachedClient } from "helpers";
import { UserRole } from "./features";

export interface FilterConfiguration {
  id: string;
  label: string;
  description?: string;
  layer: string;
  endpoint: string;
  activeWhenParametersContain: string[];
}

export enum FilterType {
  range = "range",
  singleselect = "singleselect",
  multiselect = "multiselect",
  rangeWithMarks = "range_with_marks",
  NoData = "NO_DATA",
}

interface FilterChoice {
  value: string;
  label: string;
  color?: string;
}

interface Filter {
  id: string;
  layer: string;
  icon: string;
}

export interface SelectFilter extends Filter {
  type: FilterType.singleselect | FilterType.multiselect;
  choices: FilterChoice[];
}

export interface RangeMark {
  value: number;
  label: string;
}

export interface RangeFilter extends Filter {
  type: FilterType.range;
  min: number;
  max: number;
  marks: RangeMark[] | undefined;
}

export interface UnsupportedFilter extends Filter {
  id: string;
  type: FilterType.NoData;
}

export type FilterDefinition = RangeFilter | SelectFilter | UnsupportedFilter;

class FilterStore {
  private readonly appStore: AppStore;

  public filterParameter: string | "" = "";
  public searchResults: SearchResult = { coordinates: [], length: 0 };
  public isLoading: boolean = false;
  private readonly cached: CachedClient;

  constructor(appStore: AppStore) {
    makeAutoObservable(this);
    this.appStore = appStore;
    this.cached = new CachedClient(this);

    if (this.appStore.userRole === UserRole.Public) return;

    reaction(
      () => this.filterParameter,
      () => {
        this.searchBuildings();
      },
    );

    reaction(
      () => this.filterConfiguration,
      () => {
        this.searchBuildings();
      },
    );

    this.filterParameter = this.getUrlParams().toString();
  }

  setSearchResult(searchResult: SearchResult): void {
    this.searchResults = searchResult;
  }

  get client(): AxiosInstance {
    return this.appStore.selectedProject.client;
  }

  get filterConfiguration(): FilterConfiguration[] | undefined {
    if (this.appStore.userCanUseFilter) {
      return this.cached.get("/eep/filter/configuration/");
    }
    return [];
  }

  async getFieldDetails(
    fieldId: string,
  ): Promise<AxiosResponse<FilterDefinition>> {
    return await this.appStore.selectedProject.client.get(
      `/eep/filter/configuration/${fieldId}/`,
    );
  }

  hasBuildingFiltersSelected(): boolean {
    return Object.values(this.getQueryParameterGroupedByEndpoint()).length > 0;
  }

  hasFiltersSelected(): boolean {
    return this.filterParameter.length > 1;
  }

  getQueryParameterGroupedByEndpoint(
    layer: string = "filterIconLayer",
    initialDelimiter: string = "?",
  ): Map<string, string> {
    const groupedByEndpoint = {};
    const params = this.getUrlParams();

    this.filterConfiguration
      ?.filter((configuration) => configuration.layer === layer)
      .forEach((configuration: FilterConfiguration) => {
        configuration.activeWhenParametersContain.forEach(
          (parameter: string) => {
            if (params.has(parameter)) {
              if (configuration.endpoint in groupedByEndpoint) {
                groupedByEndpoint[configuration.endpoint] =
                  groupedByEndpoint[configuration.endpoint] +
                  `&${parameter}=${params.get(parameter)}`;
              } else {
                groupedByEndpoint[configuration.endpoint] =
                  `${initialDelimiter}${parameter}=${params.get(parameter)}`;
              }
            }
          },
        );
      });

    return groupedByEndpoint;
  }

  searchBuildings(): void {
    runInAction(() => {
      this.isLoading = true;
    });

    const groupedByEndpoint = this.getQueryParameterGroupedByEndpoint();

    const apiCalls = Object.keys(groupedByEndpoint).map(async (endpoint) => {
      const queryString = groupedByEndpoint[endpoint];
      return await apiCall({
        url: `/api/v1/project/${this.appStore.appStore.selectedProject.uuid}/${endpoint}/${queryString}&format=plaincoordinates`,
        accessToken: this.appStore.userStore.token?.access,
      });
    });

    void Promise.all(apiCalls)
      .then((res: number[][]) => {
        let result: number[] = [];
        res.forEach((r: number[]) => (result = [...result, ...r]));
        this.setSearchResult({
          coordinates: result,
          length: result.length / 2,
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  }

  getUrlParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  setUrlParams(parameter: string): void {
    this.filterParameter = parameter;
    window.history.pushState({}, "", `?${parameter}`);
  }
}

export default FilterStore;
